import React from 'react';
import { motion } from 'framer-motion';
import { Target, Eye, Heart, Award, Users, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';

const teamImage = "https://customer-assets.emergentagent.com/job_d8692872-ce9e-4544-878c-3a5b2f1a67b0/artifacts/j9nmjbxt_plaquette-gtic-ok-final%20%282%29-19.png";

export default function AboutPage() {
  const { t } = useLanguage();

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  const values = [
    { icon: Award, title: 'Excellence', description: 'Nous visons la perfection dans chaque projet' },
    { icon: Heart, title: 'Intégrité', description: 'Honnêteté et transparence dans toutes nos relations' },
    { icon: Target, title: 'Innovation', description: 'Toujours à la recherche de nouvelles solutions' },
    { icon: Users, title: 'Travail d\'équipe', description: 'La force du collectif pour des résultats exceptionnels' },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-[#0F172A]">
        <div className="absolute inset-0 opacity-20">
          <img src={teamImage} alt="Team" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <span className="inline-block px-4 py-1 bg-[#0B6AB3]/20 text-[#0B6AB3] text-sm font-medium rounded-full mb-4">
              Qui sommes-nous
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-white font-['Montserrat']">
              {t('about_title')}
            </h1>
            <p className="text-gray-300 mt-4 max-w-2xl mx-auto text-lg">
              Votre partenaire de confiance depuis 2015
            </p>
          </motion.div>
        </div>
      </section>

      {/* History Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <span className="text-[#0B6AB3] font-medium uppercase tracking-wider text-sm">
                Notre parcours
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#0F172A] mt-2 font-['Montserrat']">
                {t('about_history')}
              </h2>
              <p className="text-gray-600 mt-6 leading-relaxed">
                {t('about_history_text')}
              </p>
              <p className="text-gray-600 mt-4 leading-relaxed">
                Au fil des années, nous avons développé une expertise reconnue dans le secteur du BTP, 
                de la communication et des services d'impression. Notre croissance constante est le 
                fruit de notre engagement envers la qualité et la satisfaction de nos clients.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-[#F8F9FA] rounded-lg">
                  <div className="text-3xl font-bold text-[#0B6AB3]">2015</div>
                  <div className="text-sm text-gray-600 mt-1">Année de création</div>
                </div>
                <div className="text-center p-4 bg-[#F8F9FA] rounded-lg">
                  <div className="text-3xl font-bold text-[#0B6AB3]">150+</div>
                  <div className="text-sm text-gray-600 mt-1">Projets réalisés</div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <img
                src={teamImage}
                alt="GTIC Team"
                className="rounded-lg shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#0B6AB3] text-white p-6 rounded-lg shadow-lg">
                <div className="text-4xl font-bold">10+</div>
                <div className="text-sm mt-1">Années d'expérience</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 lg:py-28 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <Card className="h-full border-0 shadow-sm bg-white" data-testid="vision-card">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-[#0B6AB3]/10 rounded-lg flex items-center justify-center mb-6">
                    <Eye className="h-7 w-7 text-[#0B6AB3]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#0F172A] mb-4 font-['Montserrat']">
                    {t('about_vision')}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {t('about_vision_text')}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ delay: 0.1 }}
            >
              <Card className="h-full border-0 shadow-sm bg-white" data-testid="mission-card">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-[#E50E24]/10 rounded-lg flex items-center justify-center mb-6">
                    <Target className="h-7 w-7 text-[#E50E24]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#0F172A] mb-4 font-['Montserrat']">
                    {t('about_mission')}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {t('about_mission_text')}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <span className="text-[#0B6AB3] font-medium uppercase tracking-wider text-sm">
              Ce qui nous guide
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#0F172A] mt-2 font-['Montserrat']">
              {t('about_values')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <motion.div
                  key={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full border-0 shadow-sm hover:shadow-lg transition-all duration-300 text-center bg-white" data-testid={`value-card-${index}`}>
                    <CardContent className="p-6">
                      <div className="w-16 h-16 bg-[#0B6AB3]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <IconComponent className="h-8 w-8 text-[#0B6AB3]" />
                      </div>
                      <h3 className="font-semibold text-lg text-[#0F172A] mb-2">
                        {value.title}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {value.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 lg:py-28 bg-[#0B6AB3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white font-['Montserrat']">
              Pourquoi nous choisir ?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Équipe expérimentée et qualifiée',
              'Respect des délais et des budgets',
              'Matériaux et équipements de qualité',
              'Service client réactif',
              'Solutions sur mesure',
              'Garantie de satisfaction',
            ].map((item, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
                className="flex items-center space-x-3 bg-white/10 rounded-lg p-4"
                data-testid={`why-us-${index}`}
              >
                <CheckCircle className="h-6 w-6 text-white flex-shrink-0" />
                <span className="text-white font-medium">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
